import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import AddMediaIcon from 'components/Icon/AddMediaIcon';
import SubmitIcon from 'components/Icon/SubmitIcon';
import ImageUpload from 'components/ImageUpload';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageForm from 'components/MessageForm';
import { MESSAGE_TYPE_IMAGE_CAPTION } from 'components/MessageForm';
import TextButton from 'components/TextButton';
import { getToken } from 'utils/auth';
import { getCredentialsSetting } from 'utils/cookies';
import { isAdmin } from 'utils/roles';
import { userShape } from 'utils/shapes';
import ModalCloseIcon from 'resources/icon-modal-close.svg';
import './ImageUploadModal.css';

class ImageUploadModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      message: '',
      imageAltText: '',
      file: null,
      uploadRequested: false,
      uploadFailedError: null,
      uploadRequiredError: false,
      showEmojiPicker: false
    };
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleMessageChange = this.handleMessageChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleImageDrop = this.handleImageDrop.bind(this);
    this.handleImageClear = this.handleImageClear.bind(this);
    this.onClose = this.onClose.bind(this);
    this.toggleEmojiPicker = this.toggleEmojiPicker.bind(this);
    this.addEmoji = this.addEmoji.bind(this);
    this.logUserEvent = this.logUserEvent.bind(this);
  }

  handleInputChange(event) {
    this.setState({
      [event.target.name]: event.target.value,
      uploadRequiredError: false
    });
  }

  handleMessageChange(event) {
    this.setState({
      message: event.target.value,
      uploadRequiredError: false
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' && event.shiftKey === false) {
      this.handleSubmit(event);
    }
  }

  logUserEvent(eventValue) {
    trackUserEvent({
      activityType: SELECTION_EVENT,
      eventName: 'upload-picture',
      eventValue: eventValue,
      initiator: 'add-picture-submit-button'
    });
  }

  handleSubmit(event) {
    event.preventDefault();

    const { user, resourcePath } = this.props;
    const { file, imageAltText } = this.state;
    const valid = file && (!user || !isAdmin(user) || imageAltText.trim());
    if (valid) {
      this.setState({
        uploadRequested: true
      });

      const paths = Array.isArray(resourcePath) ? resourcePath : [resourcePath];
      Promise.all(paths.map(path => this.uploadImage(path)))
        .then(() => {
          this.onClose();
          this.logUserEvent('success');
        })
        .catch(error => {
          this.setState({
            uploadFailedError: error,
            uploadRequested: false
          });
          this.logUserEvent('failed');
        });
    } else {
      this.setState({
        uploadRequiredError: true
      });
      this.logUserEvent('error');
    }
  }

  handleImageDrop(file) {
    this.setState({
      file,
      uploadFailedError: null,
      uploadRequiredError: false
    });
  }

  handleImageClear() {
    this.setState({
      file: null,
      uploadRequiredError: false
    });
  }

  uploadImage(resourcePath) {
    const token = getToken();
    const { file } = this.state;

    const formData = new FormData();
    formData.append('picture', file);

    return fetch(process.env.REACT_APP_API_URI + resourcePath, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      credentials: getCredentialsSetting(),
      body: formData
    })
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          return response;
        } else {
          const error = new Error(response.statusText);
          error.response = response;
          throw error;
        }
      })
      .then(response => {
        return response.json();
      })
      .then(json => {
        const { message, imageAltText } = this.state;
        const { applyImage } = this.props;

        if (json.imageKey) {
          applyImage(
            json.imageKey || null,
            message.trim() || null,
            json.imageUrl || null,
            imageAltText.trim() || null,
            resourcePath || null
          );
        } else {
          throw new Error('Failed to retrieve imageKey from upload result');
        }
      });
  }

  onClose() {
    const { onHide } = this.props;

    this.setState({
      message: '',
      imageAltText: '',
      uploadRequested: false,
      file: null,
      uploadFailedError: null
    });

    onHide();
  }

  toggleEmojiPicker() {
    const isEmoji = this.state.showEmojiPicker;
    this.setState({
      showEmojiPicker: !isEmoji
    });
  }

  addEmoji(emoji) {
    const { message } = this.state;
    const text = `${message}${emoji.native}`;

    this.setState({
      message: text
    });
  }

  renderImageAltTextInput(includeComment, uploadRequested) {
    const { user } = this.props;
    const { imageAltText } = this.state;
    const required = user && isAdmin(user);
    const validationState = required && !imageAltText.trim() ? 'error' : null;
    return (
      includeComment &&
      required && (
        <div className="image-alt-form">
          <FormGroup validationState={validationState}>
            <FormLabel>Image alternative text{required ? '*' : ''}</FormLabel>
            <FormControl
              type="text"
              disabled={uploadRequested}
              name="imageAltText"
              value={imageAltText}
              onChange={this.handleInputChange}
              maxLength={4096}
              placeholder="Text for screen readers"
              aria-label="Text input for screen readers"
            />
          </FormGroup>
        </div>
      )
    );
  }

  renderFooter(includeComment, uploadRequested) {
    if (includeComment) {
      return (
        <Modal.Footer>
          <div className="feed-comment-container image-caption-form">
            <MessageForm
              onSubmit={this.handleSubmit}
              onChange={this.handleMessageChange}
              message={this.state.message}
              placeholder={MESSAGE_TYPE_IMAGE_CAPTION}
              disabled={uploadRequested}
              onEmojiPicker={this.toggleEmojiPicker}
              showEmojiPicker={this.state.showEmojiPicker}
              addEmoji={this.addEmoji}
            />
          </div>
        </Modal.Footer>
      );
    } else {
      return (
        <Modal.Footer>
          <div className="modal-submit-container image-caption-form">
            <button
              type="button"
              title="Submit"
              disabled={this.state.file == null}
              onClick={this.handleSubmit}
              onKeyDown={this.handleKeyDown}
            >
              <SubmitIcon className="modal-submit-btn" alt="Submit Image" />
            </button>
          </div>
        </Modal.Footer>
      );
    }
  }

  render() {
    const {
      onHide,
      applyImage,
      includeComment,
      resourcePath,
      helpText,
      ...otherProps
    } = this.props;
    const {
      uploadRequested,
      uploadFailedError,
      uploadRequiredError,
      file,
      imageAltText
    } = this.state;

    return (
      <Modal dialogClassName="image-upload-modal" {...otherProps}>
        <Modal.Header>
          <AddMediaIcon label="Upload a Picture" alt="" />
          <div className="icon-modal-close-container">
            <TextButton className="modal-close-button" onClick={this.onClose}>
              <img src={ModalCloseIcon} alt="Close Modal" />
            </TextButton>
          </div>
        </Modal.Header>
        <Modal.Body>
          {uploadFailedError && (
            <Alert variant="danger">
              {uploadFailedError.response &&
              uploadFailedError.response.status === 413
                ? 'The uploaded file is too large.'
                : 'The file upload failed.'}
            </Alert>
          )}
          {uploadRequested && <LoadingIndicator overlay />}
          {uploadRequiredError && (
            <Alert variant="danger">
              {!file && !imageAltText.trim()
                ? 'Please upload a picture and enter the picture alternative text'
                : file && !imageAltText.trim()
                ? 'Please enter the picture alternative text'
                : 'Please upload a picture'}
            </Alert>
          )}
          <ImageUpload
            handleImageDrop={this.handleImageDrop}
            handleImageClear={this.handleImageClear}
          />
          {helpText && <Alert variant="info">{helpText}</Alert>}
          {this.renderImageAltTextInput(includeComment, uploadRequested)}
        </Modal.Body>
        {this.renderFooter(includeComment, uploadRequested)}
      </Modal>
    );
  }
}

ImageUploadModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  applyImage: PropTypes.func.isRequired,
  resourcePath: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
    .isRequired,
  includeComment: PropTypes.bool.isRequired,
  helpText: PropTypes.string,
  user: userShape,
  // Modal props
  closeButton: PropTypes.bool,
  show: PropTypes.bool
};

export default ImageUploadModal;
